import { useEffect } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';

import {
	trackWebPushPromptShown,
	trackWebPushPromptPermissionDenied,
	trackWebPushPromptPermissionAccepted,
	trackWebPushUserEvents
} from '@comeon/analytics';
import { setCookie } from '~/store/cookies.js';
import { Modal } from '~/components/modal/modal.js';
import breakpoint from '~/utils/style-utils';
import useTranslations from '~/components/translation/use-translations.ts';
import {
	isWebPushPromptVisibleSelector,
	linkEmarsysContact,
	selectorEmarsysUserSubscribed,
	checkIfUserIsSubscribedOnEmarsys,
	showWebPushPrompt,
	subscribeToWebPush,
	updateSubscribedStatus,
	selectedtextKeys
} from './ducks/web-push.js';
import { authenticationSelector } from '../authentication/ducks/selectors.js';
import { selectUsername } from '../account/ducks/index.js';
import { isWebPushDisabledForTests, showPopupPromptCondition, WEB_PUSH_COOKIE_NAME } from './web-push-utils.js';

const ModalWrapper = styled.div`
	max-width: 500px;
	margin-left: auto;
	margin-right: auto;
	align-items: center;
	justify-items: center;
	border-radius: 3px;
	background-color: rgba(var(--web-push-popup-bg), 1);
	box-shadow: 0px 0px 2px black;
	padding: 20px;
`;

const TitleWrapper = styled.div`
	font: var(--h2);
	margin-bottom: 10px;
	color: rgba(var(--web-push-popup-title), 1);
`;

const SubTitleWrapper = styled.p`
	font-size: 15px;
	line-height: 20px;
	color: rgba(var(--web-push-popup-subtitle), 1);
`;

const ButtonWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	flex-direction: column-reverse;
	justify-content: flex-end;
	padding: 10px 0;

	${breakpoint.lg`
		flex-direction: row;
	`}
`;

const Button = styled.button`
	border: ${props => (props.noBg ? 'none' : '1px solid rgba(var(--web-push-popup-button-bg), 1)')};
	color: ${props =>
		props.noBg ? 'rgba(var(--web-push-popup-button-bg), 1)' : 'rgba(var(--web-push-popup-button-text), 1)'};
	font-size: 16px;
	font-weight: bold;
	text-align: center;
	padding: 10px 45px;
	border-radius: 26px;
	margin-left: 0;
	margin-top: 10px;

	${breakpoint.lg`
		margin-left: 10px;
		margin-top: 0;
	`}
`;

const translationTexts = [
	'WEB_PUSH_TITLE',
	'WEB_PUSH_DESCRIPTION',
	'WEB_PUSH_TITLE_PAYMENT_SUCCESS',
	'WEB_PUSH_DESCRIPTION_PAYMENT_SUCCESS',
	'WEB_PUSH_TITLE_WITHDRAW_REQUEST',
	'WEB_PUSH_DESCRIPTION_WITHDRAW_REQUEST',
	'WEB_PUSH_TITLE_WE_SPIN',
	'WEB_PUSH_DESCRIPTION_WE_SPIN',
	'WEB_PUSH_NOTIFY_CTA',
	'WEB_PUSH_DO_NOT_NOTIFY_CTA'
];

const WebPush = () => {
	const dispatch = useDispatch();
	const { translations, isLoadingTranslations } = useTranslations(translationTexts);

	const reduxWebPushPromptEnabled = useSelector(isWebPushPromptVisibleSelector);
	const coolOffPeriod = useSelector(state => state.brand.webPushPermissionCooldownDays);
	const isAuthenticated = useSelector(authenticationSelector);
	const emarsysPlayerUsername = useSelector(selectUsername);
	const webPushContactFieldID = useSelector(state => state.brand.webPushContactFieldID);
	const emarsysUserSubscribed = useSelector(selectorEmarsysUserSubscribed);
	const selectedTextKeys = useSelector(selectedtextKeys);
	let titleText;
	let subtitleText;

	// This bit is done to bind the updateSubscribedStatus function to onSubscribe event
	useEffect(() => {
		if (window.WebEmarsysSdk && emarsysPlayerUsername) {
			// Callback when subscribe is fired
			window.WebEmarsysSdk.push([
				'onSubscribe',
				() => {
					dispatch(updateSubscribedStatus(true));
					dispatch(showWebPushPrompt(false));
					trackWebPushUserEvents('onSubscribe', emarsysPlayerUsername);
				}
			]);
			// Callback for sdk init error
			window.WebEmarsysSdk.push([
				'onSWInitError',
				() => {
					trackWebPushUserEvents('onSWInitError', emarsysPlayerUsername);
				}
			]);

			// Callback for when browser permission promt is shown
			window.WebEmarsysSdk.push([
				'onPermissionPrompt',
				() => {
					trackWebPushUserEvents('onPermissionPrompt', emarsysPlayerUsername);
				}
			]);

			// Callback for when User grants permission on browser prompt
			window.WebEmarsysSdk.push([
				'onPermissionGranted',
				() => {
					trackWebPushUserEvents('onPermissionGranted', emarsysPlayerUsername);
				}
			]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emarsysPlayerUsername]);

	useEffect(() => {
		// Conditions
		// showPrompt true in redux
		// not in COOLDOWN
		// User not already subscribed to emarsys
		// The user is not using iOS mobile
		if (showPopupPromptCondition(reduxWebPushPromptEnabled, coolOffPeriod, emarsysUserSubscribed)) {
			trackWebPushPromptShown('manual');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reduxWebPushPromptEnabled]);

	useEffect(() => {
		if (isAuthenticated) {
			dispatch(checkIfUserIsSubscribedOnEmarsys());
			if (emarsysPlayerUsername) {
				// Calling Get logged in info inside linkEmarsysContact when the user has been logged in
				linkEmarsysContact(webPushContactFieldID, emarsysPlayerUsername);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated]);

	const handleDenyNotificationPermission = () => {
		trackWebPushPromptPermissionDenied('manual');
		dispatch(showWebPushPrompt(false));
		// Set cookie for current date
		setCookie(WEB_PUSH_COOKIE_NAME, new Date());
	};

	const handleAllowNotificationPermission = () => {
		// Do the API Call to enable the web-push notification here
		subscribeToWebPush();
		trackWebPushPromptPermissionAccepted('manual');
		dispatch(showWebPushPrompt(false));
	};

	if (isLoadingTranslations) {
		return <div className="spinner" />;
	}

	// Selecting which text pairs to display
	switch (selectedTextKeys) {
		case `successful-deposit`:
			titleText = translations.WEB_PUSH_TITLE_PAYMENT_SUCCESS;
			subtitleText = translations.WEB_PUSH_DESCRIPTION_PAYMENT_SUCCESS;
			break;

		case `withdraw-request`:
			titleText = translations.WEB_PUSH_TITLE_WITHDRAW_REQUEST;
			subtitleText = translations.WEB_PUSH_DESCRIPTION_WITHDRAW_REQUEST;
			break;

		case `we-spin-success`:
			titleText = translations.WEB_PUSH_TITLE_WE_SPIN;
			subtitleText = translations.WEB_PUSH_DESCRIPTION_WE_SPIN;
			break;

		default:
			titleText = translations.WEB_PUSH_TITLE;
			subtitleText = translations.WEB_PUSH_DESCRIPTION;
			break;
	}

	return (
		<Modal
			show={
				!isWebPushDisabledForTests() &&
				showPopupPromptCondition(reduxWebPushPromptEnabled, coolOffPeriod, emarsysUserSubscribed)
			}
			blurBg
		>
			<ModalWrapper>
				<TitleWrapper>{titleText}</TitleWrapper>
				<SubTitleWrapper>{subtitleText}</SubTitleWrapper>
				<ButtonWrapper>
					<Button noBg onClick={handleDenyNotificationPermission}>
						{translations.WEB_PUSH_DO_NOT_NOTIFY_CTA}
					</Button>
					<Button onClick={handleAllowNotificationPermission}>{translations.WEB_PUSH_NOTIFY_CTA}</Button>
				</ButtonWrapper>
			</ModalWrapper>
		</Modal>
	);
};

export default WebPush;
